import { LoaderCircle } from 'lucide-react'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader as DialogHeaderComponent,
    DialogTitle as DialogTitleComponent,
} from '@/components/ui/dialog'

// Type casts for the dialog parts from a JSX file, whose className is otherwise required
const DialogHeader = DialogHeaderComponent as React.FC<React.HTMLAttributes<HTMLDivElement>>
const DialogTitle = DialogTitleComponent as React.FC<React.HTMLAttributes<HTMLHeadingElement>>

interface HelperDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    title: string
    description?: string
    children: React.ReactNode
    /** Actions below the scrolling body, such as KeywordHelper's "Done" */
    footer?: React.ReactNode
}

/**
 * The modal an AI helper ("Help me with ...") runs in: the license overview form first, then the suggestions. The
 * body scrolls when it is taller than the screen, as the overview form with its file fields can be.
 */
const HelperDialog = ({ open, onOpenChange, title, description, children, footer }: HelperDialogProps) => (
    <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="tw:w-2xl" {...(description ? {} : { 'aria-describedby': undefined })}>
            <DialogHeader>
                <DialogTitle>{title}</DialogTitle>
            </DialogHeader>
            {description && (
                <DialogDescription className="tw:px-6 tw:pt-4 tw:pb-0 tw:border-b-0 tw:text-gray-300">
                    {description}
                </DialogDescription>
            )}
            <div className="tw:max-h-[70vh] tw:overflow-y-auto tw:px-6 tw:py-4">{children}</div>
            {footer && <DialogFooter className="tw:border-t">{footer}</DialogFooter>}
        </DialogContent>
    </Dialog>
)

/** What a helper dialog shows while it waits for the server, such as "Generating suggestions…". */
export const HelperDialogLoading = ({ label }: { label: string }) => (
    <p role="status" className="tw:flex tw:items-center tw:gap-2 tw:text-sm tw:text-gray-300 tw:m-0">
        <LoaderCircle size={16} className="tw:animate-spin" />
        {label}
    </p>
)

export default HelperDialog
